<template>
  <main class="call-history">
    <div class="call-history__body">
      <h1 class="call-history__title">История звонков</h1>
      <div class="call-history__filters">
        <form class="call-history__filters__form">
          <date-range-with-presets
            v-bind:presets="periodPresets"
            v-model="selectedPeriod"
            custom-color-date="black"
            v-on:close="reloadCallHistory"
            v-on:input-preset="onInputPreset"
          />
          <div class="call-history__filters__row">
            <input-autocomplete
              clearable
              hide-details
              option-title-key="name"
              option-value-key="id"
              title="ФИО сотрудника"
              v-bind:options="employeeList"
              v-bind:value="selectedEmployeeId"
              v-on:input="onSelectEmployeeId"
            />
            <input-autocomplete
              clearable
              hide-details
              option-title-key="name"
              option-value-key="id"
              title="ФИО пациента"
              v-bind:options="patientList"
              v-bind:value="selectedPatientId"
              v-on:input="onSelectPatientId"
            />
          </div>
        </form>
        <div class="call-history__groups">
          <label
            v-for="{ count, title, value } in callGroups"
            v-bind:key="value"
            class="call-history__group"
            v-bind:class="value"
          >
            <input
              hidden
              type="radio"
              v-bind:id="value"
              v-bind:value="value"
              v-model="selectedCallGroup"
              v-on:change="reloadCallHistory"
            />
            <span class="call-history__group__label">
              <div
                v-if="value !== $options.groupAll"
                class="call-history__icon"
                v-bind:class="value"
              />
              {{title}} {{count}}
            </span>
          </label>
        </div>
      </div>
      <div class="call-history__scroll">
        <div class="call-history__table">
          <div class="call-history__table__head">
            <div class="call-history__table__cell">Дата и время звонка</div>
            <div class="call-history__table__cell">Сотрудник</div>
            <div class="call-history__table__cell">Телефон</div>
            <div class="call-history__table__cell">Пациент</div>
            <div class="call-history__table__cell">Приём</div>
            <div class="call-history__table__cell">Длительность</div>
            <div class="call-history__table__cell">Запись</div>
          </div>
          <div class="call-history__table__body">
            <div
              class="call-history__table__row"
              v-for="call in callHistory"
              v-bind:key="call.id"
            >
              <div
                class="call-history__table__cell call-type"
                data-label="Дата и время звонка"
              >
                <span class="call-history__icon" v-bind:class="call.type" v-bind:title="call.typeName" />
                <span>{{ call.date }}</span>
                <span>{{ call.time }}</span>
              </div>
              <div class="call-history__table__cell" data-label="Сотрудник">
                {{ call.employeeName }}
              </div>
              <div class="call-history__table__cell" data-label="Телефон">{{ call.phone }}</div>
              <router-link
                v-if="call.patientId"
                v-bind:to="makeNavigationToPatient(call.patientId)"
                class="call-history__table__cell link"
                data-label="Пациент"> {{ call.patientName }}</router-link>
              <div v-else class="call-history__table__cell" data-label="Пациент"> {{ call.patientName }}</div>
              <router-link
                v-if="call.appointment.id"
                class="call-history__table__cell link"
                v-bind:to="makeNavigationToAppointment(call.appointment)"
                data-label="Приём"
              >{{ call.appointment.title }}</router-link>
              <div
                v-else
                class="call-history__table__cell"
                data-label="Приём"
              >{{ call.appointment.title }}</div>
              <div class="call-history__table__cell" data-label="Длительность">
                {{ call.duration }}
              </div>
              <div
                v-if="call.recordFileLink"
                class="call-history__table__cell record"
                data-label="Запись">
                <button
                  class="call-history__icon play"
                  title="Прослушать"
                  type="button"
                  v-on:click="onPlayRecord(call.recordFileLink)"
                />
                <a
                  aria-label="Скачать запись разговора"
                  class="call-history__icon download"
                  download="file"
                  rel="noopener noreferrer"
                  target="_blank"
                  title="Скачать запись разговора"
                  type="button"
                  v-bind:href="call.recordFileLink"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="isEmptyHistory" class="call-history__result">Нет звонков</div>
    </div>
    <div v-if="recordFileLink" class="call-history__audio">
      <audio class="call-history__audio__player" autoplay controls v-bind:src="recordFileLink">
        <track kind="captions" />
      </audio>
    </div>
  </main>
</template>

<script>
import DateRangeWithPresets from '@/components/DateRangeWithPresets.vue';
import InputAutocomplete from '@/components/InputAutocomplete.vue';
import { ViewCallHistoryRequest } from '@/storage/gateways/TelephonyIntegrationGateway';
import notifierMixin from '@/mixins/notifierMixin';

const GROUP_ALL = 'all';

export default {
  groupAll: GROUP_ALL,
  components: { DateRangeWithPresets, InputAutocomplete },
  computed: {
    /**
     * @description Предварительные установки периодов.
     * @returns {{toValue: function(): [Date,Date], id: string, label: string, title: string}[]}
     */
    periodPresets() {
      return this.periodTypes.map(({ periodDates, title, value }) => ({
        id: value,
        label: title,
        title,
        toValue: () => periodDates,
      }));
    },
    isEmptyHistory() {
      return !this.isAwaitLoading && this.callHistory.length === 0;
    },
  },
  created() {
    this.fetchOptions();
    this.reloadCallHistory();
  },
  data() {
    const toDay = new Date();
    return {
      isAwaitLoading: false,
      callGroups: [],
      callHistory: [],
      employeeList: [],
      patientList: [],
      periodTypes: [],
      recordFileLink: null,
      selectedEmployeeId: null,
      selectedPatientId: null,
      selectedCallGroup: GROUP_ALL,
      selectedPeriod: [toDay, toDay],
    };
  },
  methods: {
    fetchOptions() {
      return this
        .$telephonyIntegrationGateway
        .viewFilterForCallHistory()
        .then((result) => {
          result.forEach((options) => {
            this.patientList = options.patients;
            this.employeeList = options.employees;
            this.periodTypes = options
              .period_types
              .map(({ from_to: [from, to], title, value }) => ({
                periodDates: [new Date(from), new Date(to)],
                title,
                value,
              }));
          });
        });
    },
    makeNavigationToPatient(patientId) {
      return { name: 'ClinicPanelPatientBasic', params: { id: patientId } };
    },
    makeNavigationToAppointment(appointment) {
      return {
        name: 'ClinicPanelAppointmentScheduleEmployeePeriod',
        params: {
          id: appointment.employeeId,
        },
        query: {
          appointment: appointment.id,
          day: appointment.dateIso,
        },
      };
    },
    reloadCallHistory() {
      this.isAwaitLoading = true;
      const onFail = (error) => this.notifyFail(error.getTitle());
      const onSuccess = (response) => {
        this.callGroups = response.callGroups;
        this.callHistory = response.callHistory;
      };
      const request = new ViewCallHistoryRequest(
        this.selectedCallGroup,
        this.selectedPeriod,
        this.selectedEmployeeId,
        this.selectedPatientId,
      );
      this
        .$telephonyIntegrationGateway
        .viewCallHistory(request)
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitLoading = false; });
    },
    onPlayRecord(recordFileLink) {
      this.recordFileLink = recordFileLink;
    },
    onSelectEmployeeId(value) {
      this.selectedEmployeeId = value;
      this.reloadCallHistory();
    },
    onSelectPatientId(value) {
      this.selectedPatientId = value;
      this.reloadCallHistory();
    },
    onInputPreset(preset) {
      if (preset !== 'range') {
        this.reloadCallHistory();
      }
    },
  },
  name: 'ClinicPanelCallHistory',
  mixins: [notifierMixin],
};
</script>

<style scoped lang="scss">

.call-history {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow-y: scroll;
  &__body {
    padding: 38px 20px 48px 23px;
    width: 100%;
  }
  &__title {
    margin: 0 0 45px 0;
  }
  &__filters {
    display: flex;
    flex-direction: column;
    gap: 37px;
    max-width: 1283px;
    &__form {
      display: flex;
      flex-flow: row wrap;
      justify-content: space-between;
      align-items: center;
      column-gap: 90px;
      row-gap: 20px;
      background: #F2F5F8;
      padding: 40px 34px 34px 28px;
    }
    &__row {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 66px;
      margin: -7px 0 0 0;
    }
  }
  &__groups {
    display: flex;
    gap: 6px;
  }
  &__group {
    display: flex;
    align-items: center;
    &>input {
      position: absolute;
      z-index: -1;
      opacity: 0;
      &:checked+span {
        border: 1px solid #0489E0;
      }
    }
    &>span {
      display: flex;
      align-items: center;
      gap: 9px;
      border-radius: 4px;
      border: 1px solid #C2CFE0;
      cursor: pointer;
      padding: 8px;
      font-size: 14px;
      &.all {
        padding:8px 20px;
      }
      &:hover {
        border: 1px solid #0489E0;
      }
    }
  }
  &__icon {
    align-self: center;
    background-repeat: no-repeat;
    background-size: contain;
    width: 20px;
    height: 20px;
    margin: 0;
    padding: 0;

    &.incoming {
      background-image: url("@/assets/img/call/incoming.svg");
    }
    &.missed {
      background-image: url("@/assets/img/call/missed.svg");
    }
    &.outgoing {
      background-image: url("@/assets/img/call/outgoing.svg");
    }
    &.play {
      background-image: url("@/assets/img/call/play.svg");
    }
    &.download {
      background-image: url("@/assets/img/call/download.svg");
    }
  }
  &__table {
    margin-top: 35px;
    display: flex;
    flex-direction: column;
    width: 100%;
    &__body {
      width: 100%;
    }
    &__head,
    &__row {
      display: grid;
      grid-template-columns: 170px 1fr 1fr 1fr 1fr 1fr 100px;
      font-size: 14px;
    }
    &__row {
      text-align: left;
      border-bottom: 1px solid #eaeff3;
      cursor: default;
    }
    &__head {
      font-weight: 700;
      border-bottom: 2px solid #0489e0;
    }
    &__cell {
      transition: color var(--animate-duration);
      padding: 12px 8px;
      display: flex;
      align-items: center;
      color: rgba(0,0,0,.87) !important;
      &:hover {
        text-decoration: none !important;
      }
      &.call-type {
        display: flex;
        gap: 10px;
      }
      &.bold {
        font-weight: 700;
      }
      &.record {
        display: flex;
        gap: 30px;
      }
    }
  }
  &__result {
    margin: 20px 0 0 0;
  }
  &__audio {
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    &__player {
      width: 100%;
    }
  }
}
@media screen and (max-width: 1020px) {
  .call-history {
    &__filters {
      .date-range-with-presets {
        flex-flow: row wrap;
      }
    }
    &__table {
      width: 1020px;
    }
    &__scroll {
      overflow: scroll;
    }
  }
}
@media screen and (max-width: 600px) {
  .call-history {
    &__filters {
      &__row {
        flex-direction: column;
        gap: 20px;
        margin: 0;
        width: 100%;
      }
    }
  }
}
</style>
